import { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Dropdown, Popover, Rate, Select } from 'antd'
import { ClearOutlined, CloseOutlined, EditOutlined, PlusOutlined, MoreOutlined, FileAddOutlined, SendOutlined, StopOutlined, RollbackOutlined, PhoneOutlined, MailOutlined, LinkOutlined, HolderOutlined, SettingOutlined } from '@ant-design/icons'
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS as DndCSS } from '@dnd-kit/utilities'
import moment from 'moment'

import { IApplicationTableColumn, IApplicationsList } from '@api/applications/state.types'
import { IApplicationsPage } from './types'
import { Button, Checkbox, Empty, Input, ToolTip, Loader } from '@ui/index'
import { SelectDate } from '../../components/primitive/calendar'
import { IconClearClose, IconDateEdit, IconFilter } from '@img/icons'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import imgEmpty from '@img/empty.svg'
import imgAccessDenied from '@img/access_denied.svg'
import buttonCss from '@ui/button/index.module.scss'
import inputCss from '@ui/input/index.module.scss'
import {
    IApplicationTableColumnSettings,
    loadApplicationTableColumnSettings,
    moveApplicationTableColumn,
    normalizeApplicationTableColumnSettings,
    saveApplicationTableColumnSettings,
} from './table-columns-storage'
import css from './ui.table.module.scss'

type ApplicationsTableProps = Pick<IApplicationsPage, 'applicationsList' | 'viewedDelayedActionKeys' | 'handleOpenCardApplication' | 'handleOpenModalConfirm' | 'handleOpenModalMailings'> & {
    columns: IApplicationTableColumn[]
    loginName: string
    filters: IApplicationsPage['columnFilters']
    isAccessDenied: boolean
    isLoading?: boolean
    onFilterChange: IApplicationsPage['onColumnFilterChange']
    onClearFilters: () => void
    loadFilterOptions: IApplicationsPage['loadColumnFilterOptions']
    renderContactAction: (item: IApplicationsList) => ReactNode
}

type FilterOptionsState = {
    status: 'loading' | 'ready' | 'error'
    values: string[]
    message?: string
}

type ClientFilterName = 'client_name' | 'client_phone' | 'client_email'
type DateRangeDraft = { from: string, to: string }
type FilterEntry = { name: string, values: string[], key: string, valueIndex: number, editorKey: string }

const clientFilterOptions: { name: ClientFilterName, label: string }[] = [
    { name: 'client_name', label: 'Имя' },
    { name: 'client_phone', label: 'Телефон' },
    { name: 'client_email', label: 'Email' },
]
const clientFilterNames = clientFilterOptions.map(option => option.name)
const centeredColumnNames = new Set([
    'application_status_name', 'volume_hours', 'date_create', 'date_next_call', 'is_repeat', 'is_black_list',
    'course_program_id', 'sum', 'sum_month', 'duration_education', 'contract_count',
])
const dateRangeColumnNames = new Set(['date_create', 'date_next_call', 'date_change', 'start_education_date', 'end_education_date'])
const dateStringColumnNames = new Set(['date_next_call', 'start_education_date', 'end_education_date'])
const emptyDateRange: DateRangeDraft = { from: '', to: '' }
const dateRangePrefixes = { from: 'from:', to: 'to:' } as const
const dateRangeDisplayFormat = 'DD.MM.YYYY'
const dateRangeStorageFormat = 'YYYY-MM-DD'

const columnLabels: Record<string, string> = {
    application_id: '№ заявки', client_name: 'Имя клиента', client_phone: 'Телефон', client_phone_clean: 'Телефон без форматирования',
    client_email: 'Email', client_rating: 'Рейтинг', application_comment: 'Комментарий',
    application_status_name: 'Статус', application_status_id: 'ID статуса', application_status_color: 'Цвет статуса',
    course_name: 'Программа', course_id: 'ID курса', volume_hours: 'Часы', data_base: 'Сайт', contract_count: 'Договоры',
    date_create: 'Дата создания', date_change: 'Дата изменения заявки', date_next_call: 'Отложенное действие', time_next_call: 'Время действия',
    is_repeat: 'Повторная заявка', is_black_list: 'Чёрный лист', is_deleted: 'Удалена',
    preferred_contact_method: 'Способ связи', max_chat_url: 'Чат MAX', telegram_chat_url: 'Чат Telegram',
    start_education_date: 'Начало обучения', end_education_date: 'Окончание обучения', duration_education: 'Срок обучения',
    page_canonical: 'Ссылка на лэндинг курса', requirement_name: 'Наименование требования к образованию',
    course_program_id: 'Идентификатор программы',
    program_name: 'Название программы', program_name_id: 'ID программы', program_type: 'Вид программы', program_type_id: 'ID вида программы',
    program_type_name: 'Вид программы', program_sum: 'Стоимость', sum: 'Сумма', sum_month: 'Сумма в месяц', price: 'Цена',
    attestation_type_name: 'Аттестация', attestation_type_id: 'ID аттестации',
    application_type_name: 'Тип заявки', application_type_id: 'ID типа заявки',
    application_source_name: 'Источник', application_source_id: 'ID источника',
    application_origin: 'Источник заявки',
    user_demo_name_id: 'Демо-пользователь', user_demo_hash_id: 'Хэш имени пользователя для демо доступа', user_demo_crm: 'Демо CRM', sms: 'SMS',
    operator_name: 'Оператор', login_name: 'Логин', login_id: 'ID оператора',
}

const firstColumns = ['application_id', 'client_name', 'client_phone', 'client_email', 'application_status_name', 'course_name', 'volume_hours', 'application_comment', 'date_create', 'date_next_call']
const hiddenColumns = new Set([
    'data_base', 'site_name', 'application_type_id', 'application_source_id', 'client_rating',
    'client_phone', 'client_phone_clean', 'client_email', 'preferred_contact_method', 'requirement_id', 'program_id',
    'attestation_type_id', 'application_status_id', 'application_status_color', 'time_next_call', 'is_deleted',
    'user_demo_name_id', 'user_demo_hash_id', 'user_demo_crm', 'sms', 'AI_chat_GUID',
])
const fixedColumnName = 'application_id'
const actionsColumnWidth = 44
const fixedColumnWidth = 112
const maxColumnWidth = 1200
const resizeKeyboardStep = 10
const filterPopupPreferredWidth = 260
const filterPopupMinimumWidth = 132
const filterPopupViewportGap = 12
const getLabel = (column: IApplicationTableColumn) => columnLabels[column.name] || column.label || column.name

const getFilterPopupWidth = () => {
    if (typeof window === 'undefined') return filterPopupPreferredWidth
    const availableWidth = Math.floor(window.innerWidth - filterPopupViewportGap * 2)
    return Math.min(filterPopupPreferredWidth, Math.max(filterPopupMinimumWidth, availableWidth))
}

const parseDateRangeValues = (values: string[] = []): DateRangeDraft => values.reduce<DateRangeDraft>((range, value) => {
    const prefix = value.startsWith(dateRangePrefixes.from) ? 'from' : value.startsWith(dateRangePrefixes.to) ? 'to' : null
    if (!prefix) return range
    const storageValue = value.slice(dateRangePrefixes[prefix].length)
    const date = moment(storageValue, dateRangeStorageFormat, true)
    if (date.isValid()) range[prefix] = date.format(dateRangeDisplayFormat)
    return range
}, { ...emptyDateRange })

const serializeDateRange = ({ from, to }: DateRangeDraft) => {
    const serialize = (prefix: keyof DateRangeDraft, value: string) => {
        if (!value) return []
        const date = moment(value, dateRangeDisplayFormat, true)
        return date.isValid() ? [`${dateRangePrefixes[prefix]}${date.format(dateRangeStorageFormat)}`] : []
    }
    return [...serialize('from', from), ...serialize('to', to)]
}

const getCurrentDateRange = (period: 'week' | 'month'): DateRangeDraft => {
    const currentDate = moment()
    const unit = period === 'week' ? 'isoWeek' : 'month'
    return {
        from: currentDate.clone().startOf(unit).format(dateRangeDisplayFormat),
        to: currentDate.clone().endOf(unit).format(dateRangeDisplayFormat),
    }
}

const formatDateFilterValue = (value: string) => {
    const date = moment(value, 'YYYY-MM-DD', true)
    return date.isValid() ? date.format('DD.MM.YYYY') : value
}

const getFallbackColumnMinimumWidth = (column: IApplicationTableColumn) => {
    const titleWidth = Math.ceil(Array.from(getLabel(column)).reduce((width, character) => width + (/[A-ZА-ЯЁ0-9№]/.test(character) ? 8 : 7), 0))
    return Math.max(86, titleWidth + 24 + 5 + 20 + 12)
}

const getDefaultColumnWidth = (column: IApplicationTableColumn, minimumWidth: number) => {
    const preferredWidth = column.name === 'client_name' ? 270
        : column.name === 'course_name' ? 300
        : column.name === 'application_comment' ? 340
        : column.name === 'application_origin' ? 220
        : column.type === 'datetime' ? 175
        : column.type === 'date' ? 135
        : column.type === 'boolean' ? 130
        : column.type === 'number' ? 120
        : 180
    return Math.max(minimumWidth, preferredWidth)
}

const getSiteLogo = (databaseName: unknown) => {
    if (databaseName === 'yaskill') return { src: logoYaSkill, alt: 'YaSkill' }
    if (databaseName === 'modern_academy') return { src: logoModSkill, alt: 'ModSkill' }
    return null
}

const formatValue = (value: unknown, column: IApplicationTableColumn): string => {
    if (value === null || value === undefined || value === '') return '-'
    if (column.type === 'boolean') return Number(value) === 1 ? 'Да' : 'Нет'
    if (column.type === 'date' || column.type === 'datetime' || dateStringColumnNames.has(column.name)) {
        const date = dateStringColumnNames.has(column.name)
            ? moment(String(value), ['DD.MM.YYYY', 'YYYY-MM-DD'], true)
            : moment(String(value))
        if (date.isValid()) return date.format(column.type === 'datetime' ? 'DD.MM.YYYY HH:mm:ss' : 'DD.MM.YYYY')
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

const formatTimeValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return ''
    const time = moment(String(value), ['HH:mm:ss', 'HH:mm'], true)
    return time.isValid() ? time.format('HH:mm:ss') : String(value)
}

const getExternalUrl = (value: unknown) => {
    try {
        const url = new URL(String(value || '').trim())
        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : ''
    } catch {
        return ''
    }
}

const formatEducationDuration = (value: unknown, hours: unknown) => {
    const duration = Number(value)
    if (!Number.isFinite(duration)) return String(value)
    const isWeeks = Number(hours) <= 250
    if (!Number.isInteger(duration)) return `${duration} ${isWeeks ? 'недели' : 'месяца'}`

    const lastDigit = Math.abs(duration) % 10
    const lastTwoDigits = Math.abs(duration) % 100
    const unit = lastDigit === 1 && lastTwoDigits !== 11
        ? isWeeks ? 'неделя' : 'месяц'
        : [2, 3, 4].includes(lastDigit) && ![12, 13, 14].includes(lastTwoDigits)
            ? isWeeks ? 'недели' : 'месяца'
            : isWeeks ? 'недель' : 'месяцев'
    return `${duration} ${unit}`
}

const DateRangeField = ({ label, columnLabel, value, isDisabled, onChange }: {
    label: 'Начало' | 'Окончание'
    columnLabel: string
    value: string
    isDisabled: boolean
    onChange: (value: string) => void
}) => {
    const accessibleLabel = `${label}: ${columnLabel}`
    const actionLabel = label === 'Начало' ? 'начала' : 'окончания'
    const inputId = useId()

    return <div className={css.dateRangeField}>
        <label className={css.visuallyHidden} htmlFor={inputId}>{accessibleLabel}</label>
        <Input id={inputId} placeholder={label} type="mask" mask="99.99.2099" maskChar=""
            size="md" paddingRight="2px" width="100%" value={value} isDisabled={isDisabled}
            onChange={event => onChange(event.target.value)} suffix={
            <div className={css.dateRangeActions}>
                {value && <ToolTip title="Очистить" placement="left">
                    <Button size="xs" variant="iconInput" isDisabled={isDisabled} onClick={() => onChange('')}>
                        <IconClearClose width="16px" height="16px" />
                        <span className={css.visuallyHidden}>{`Очистить дату ${actionLabel}: ${columnLabel}`}</span>
                    </Button>
                </ToolTip>}
                <SelectDate currentDate={value} placement={label === 'Начало' ? 'bottom' : 'bottomRight'} onSelect={onChange}>
                    <ToolTip title="Выбрать" placement="left">
                        <Button size="xs" variant="iconInput" isDisabled={isDisabled}>
                            <IconDateEdit width="16px" height="16px" />
                            <span className={css.visuallyHidden}>{`Выбрать дату ${actionLabel}: ${columnLabel}`}</span>
                        </Button>
                    </ToolTip>
                </SelectDate>
            </div>
        } />
    </div>
}

const SortableColumnOption = ({ column, isSelected, isDisabled, onChange }: {
    column: IApplicationTableColumn
    isSelected: boolean
    isDisabled: boolean
    onChange: (name: string) => void
}) => {
    const checkboxId = useId()
    const isFixed = column.name === fixedColumnName
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: column.name,
        disabled: isDisabled || isFixed,
    })

    return <div ref={setNodeRef} className={`${css.columnOption} ${isFixed ? css.columnOptionFixed : ''} ${isDragging ? css.columnOptionDragging : ''}`}
        style={{ transform: DndCSS.Transform.toString(transform), transition }}>
        <button ref={setActivatorNodeRef} type="button" className={css.dragHandle} {...attributes} {...listeners}
            aria-label={isFixed ? `Столбец «${getLabel(column)}» закреплён` : `Переместить столбец «${getLabel(column)}»`}
            title={isFixed ? 'Позиция закреплена' : 'Изменить порядок'} disabled={isDisabled || isFixed}>
            <HolderOutlined aria-hidden="true" />
        </button>
        <label className={css.columnOptionLabel} htmlFor={checkboxId}>
            <Checkbox id={checkboxId} checked={isSelected} isDisabled={isDisabled || isFixed} onChange={() => onChange(column.name)} />
            <span className={css.columnOptionName}>{getLabel(column)}</span>
            {isFixed && <span className={css.fixedColumnLabel}>Закреплён</span>}
        </label>
    </div>
}

export const ApplicationsTable = ({ applicationsList, columns, loginName, filters, isAccessDenied, isLoading = false, viewedDelayedActionKeys,
    onFilterChange, onClearFilters, loadFilterOptions, renderContactAction, handleOpenCardApplication, handleOpenModalConfirm, handleOpenModalMailings }: ApplicationsTableProps) => {
    const inputPrefix = useId()
    const viewportRef = useRef<HTMLDivElement>(null)
    const scrollbarRef = useRef<HTMLDivElement>(null)
    const tableRef = useRef<HTMLTableElement>(null)
    const [openMenu, setOpenMenu] = useState<number | null>(null)
    const [openFilter, setOpenFilter] = useState<string | null>(null)
    const [columnsSettingsIsOpen, setColumnsSettingsIsOpen] = useState(false)
    const [filterDraft, setFilterDraft] = useState('')
    const [dateRangeDraft, setDateRangeDraft] = useState<DateRangeDraft>(emptyDateRange)
    const [editingFilterKey, setEditingFilterKey] = useState<string | null>(null)
    const [editingFilterDraft, setEditingFilterDraft] = useState('')
    const [editingDateRangeDraft, setEditingDateRangeDraft] = useState<DateRangeDraft>(emptyDateRange)
    const [clientFilterName, setClientFilterName] = useState<ClientFilterName>('client_name')
    const [filterPopupWidth, setFilterPopupWidth] = useState(filterPopupPreferredWidth)
    const filterFormRef = useRef<HTMLFormElement>(null)
    const filterEditorFormRef = useRef<HTMLFormElement>(null)
    const filterOptionsRef = useRef<Record<string, FilterOptionsState>>({})
    const [filterOptions, setFilterOptions] = useState<Record<string, FilterOptionsState>>({})
    const filterTriggers = useRef(new Map<string, HTMLButtonElement>())
    const filterEditorTriggers = useRef(new Map<string, HTMLButtonElement>())
    const columnsSettingsTriggerRef = useRef<HTMLDivElement>(null)
    const columnHeaders = useRef(new Map<string, HTMLTableCellElement>())
    const resizeCleanupRef = useRef<(() => void) | null>(null)
    const [tableWidth, setTableWidth] = useState(0)
    const [minimumColumnWidths, setMinimumColumnWidths] = useState<Record<string, number>>({})
    const [liveColumnWidths, setLiveColumnWidths] = useState<Record<string, number>>({})
    const menuTriggers = useRef(new Map<number, HTMLButtonElement>())
    const availableColumns = useMemo(() => columns.filter(column => !hiddenColumns.has(column.name)).sort((left, right) => {
        const leftIndex = firstColumns.indexOf(left.name)
        const rightIndex = firstColumns.indexOf(right.name)
        return (leftIndex < 0 ? firstColumns.length : leftIndex) - (rightIndex < 0 ? firstColumns.length : rightIndex)
    }), [columns])
    const availableColumnNames = useMemo(() => availableColumns.map(column => column.name), [availableColumns])
    const columnSettingsIdentity = `${loginName}\u0000${availableColumnNames.join('\u0000')}`
    const columnSettingsIdentityRef = useRef(columnSettingsIdentity)
    const [columnSettings, setColumnSettings] = useState<IApplicationTableColumnSettings>(() => (
        loadApplicationTableColumnSettings(loginName, availableColumnNames)
    ))
    const activeColumnSettings = columnSettingsIdentityRef.current === columnSettingsIdentity
        ? columnSettings
        : loadApplicationTableColumnSettings(loginName, availableColumnNames)
    const normalizedColumnSettings = normalizeApplicationTableColumnSettings(availableColumnNames, activeColumnSettings)
    const availableColumnsByName = new Map(availableColumns.map(column => [column.name, column]))
    const settingsColumns = normalizedColumnSettings.order
        .map(name => availableColumnsByName.get(name))
        .filter((column): column is IApplicationTableColumn => Boolean(column))
    const visibleColumnNames = new Set(normalizedColumnSettings.visible)
    const orderedColumns = settingsColumns.filter(column => visibleColumnNames.has(column.name))
    const visibleColumnsKey = orderedColumns.map(column => column.name).join('\u0000')
    const columnWidths = Object.fromEntries(orderedColumns.map(column => {
        if (column.name === fixedColumnName) return [column.name, fixedColumnWidth]
        const minimumWidth = minimumColumnWidths[column.name] || getFallbackColumnMinimumWidth(column)
        const width = liveColumnWidths[column.name] || normalizedColumnSettings.widths[column.name]
            || getDefaultColumnWidth(column, minimumWidth)
        return [column.name, Math.min(maxColumnWidth, Math.max(minimumWidth, width))]
    }))
    const columnWidthsKey = orderedColumns.map(column => `${column.name}:${columnWidths[column.name]}`).join('\u0000')
    const configuredTableWidth = actionsColumnWidth + orderedColumns.reduce((width, column) => width + columnWidths[column.name], 0)
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )
    const filterEntries: FilterEntry[] = Object.entries(filters).flatMap(([name, values]) => dateRangeColumnNames.has(name)
        ? values.length ? [{ name, values, key: values.join('\u0000'), valueIndex: 0, editorKey: `${name}\u0000range` }] : []
        : values.map((value, valueIndex) => ({ name, values: [value], key: value, valueIndex, editorKey: `${name}\u0000${valueIndex}\u0000${value}` })))
    const filterEntriesKey = filterEntries.map(entry => entry.editorKey).join('\u0001')
    const hasFilters = filterEntries.length > 0
    const activeFilterName = openFilter === 'client_name' ? clientFilterName : openFilter
    const draftColumn = columns.find(column => column.name === activeFilterName)
    const draftValue = filterDraft.trim()
    const isDuplicate = !!activeFilterName && !!filters[activeFilterName]?.includes(draftValue)
    const isFilterLimitReached = !!activeFilterName && (filters[activeFilterName]?.length || 0) >= 100
    const items = isAccessDenied ? [] : applicationsList.filter(item => item.viewValues)

    useEffect(() => {
        if (columnSettingsIdentityRef.current === columnSettingsIdentity) return

        columnSettingsIdentityRef.current = columnSettingsIdentity
        setColumnSettings(loadApplicationTableColumnSettings(loginName, availableColumnNames))
        setColumnsSettingsIsOpen(false)
    }, [availableColumnNames, columnSettingsIdentity, loginName])

    useEffect(() => {
        const table = tableRef.current
        if (!table) return

        const updateTableWidth = () => setTableWidth(table.scrollWidth)
        updateTableWidth()

        if (typeof ResizeObserver === 'undefined') return
        const resizeObserver = new ResizeObserver(updateTableWidth)
        resizeObserver.observe(table)
        return () => resizeObserver.disconnect()
    }, [applicationsList, columns, visibleColumnsKey, columnWidthsKey])

    useLayoutEffect(() => {
        setMinimumColumnWidths(currentWidths => {
            const nextWidths = { ...currentWidths }
            let changed = false

            for (const column of orderedColumns) {
                if (column.name === fixedColumnName) continue
                const header = columnHeaders.current.get(column.name)
                const title = header?.querySelector<HTMLElement>('[data-column-title]')
                const filterButton = header?.querySelector<HTMLElement>(`.${css.filterButton}`)
                if (!header || !title || !filterButton) continue

                const headerStyle = getComputedStyle(header)
                const labelStyle = getComputedStyle(title.parentElement as HTMLElement)
                const padding = (Number.parseFloat(headerStyle.paddingLeft) || 0) + (Number.parseFloat(headerStyle.paddingRight) || 0)
                const gap = Number.parseFloat(labelStyle.columnGap || labelStyle.gap) || 0
                const titleWidth = title.scrollWidth || title.getBoundingClientRect().width
                const filterWidth = filterButton.getBoundingClientRect().width || 24
                const measuredWidth = Math.ceil(titleWidth + filterWidth + gap + padding + 12)
                const minimumWidth = Math.min(maxColumnWidth, Math.max(getFallbackColumnMinimumWidth(column), measuredWidth))
                if (nextWidths[column.name] !== minimumWidth) {
                    nextWidths[column.name] = minimumWidth
                    changed = true
                }
            }

            return changed ? nextWidths : currentWidths
        })
    }, [columns, visibleColumnsKey]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => resizeCleanupRef.current?.(), [])

    useEffect(() => {
        if (!openFilter && !editingFilterKey) return

        const updateFilterPopupWidth = () => setFilterPopupWidth(getFilterPopupWidth())
        updateFilterPopupWidth()
        window.addEventListener('resize', updateFilterPopupWidth)
        return () => window.removeEventListener('resize', updateFilterPopupWidth)
    }, [editingFilterKey, openFilter])

    const closeFilter = (restoreFocus = false) => {
        if (restoreFocus && openFilter) filterTriggers.current.get(openFilter)?.focus()
        setOpenFilter(null)
        setFilterDraft('')
        setDateRangeDraft(emptyDateRange)
    }

    const closeFilterEditor = (restoreFocus = false) => {
        if (restoreFocus && editingFilterKey) filterEditorTriggers.current.get(editingFilterKey)?.focus()
        setEditingFilterKey(null)
        setEditingFilterDraft('')
        setEditingDateRangeDraft(emptyDateRange)
    }

    useEffect(() => {
        if (editingFilterKey && !filterEntries.some(entry => entry.editorKey === editingFilterKey)) closeFilterEditor()
    }, [editingFilterKey, filterEntriesKey]) // eslint-disable-line react-hooks/exhaustive-deps

    const closeColumnsSettings = (restoreFocus = false) => {
        setColumnsSettingsIsOpen(false)
        if (restoreFocus) columnsSettingsTriggerRef.current?.querySelector('button')?.focus()
    }

    const persistColumnSettings = (nextSettings: IApplicationTableColumnSettings) => {
        const normalizedSettings = normalizeApplicationTableColumnSettings(availableColumnNames, nextSettings)
        setColumnSettings(normalizedSettings)
        saveApplicationTableColumnSettings(loginName, normalizedSettings)
    }

    const ensureFilterOptions = async (column: IApplicationTableColumn) => {
        if (column.filterType !== 'select') return

        const cacheKey = `${loginName}\u0000${column.name}`
        if (filterOptionsRef.current[cacheKey]?.status === 'loading' || filterOptionsRef.current[cacheKey]?.status === 'ready') return

        const setOptionState = (state: FilterOptionsState) => {
            filterOptionsRef.current = { ...filterOptionsRef.current, [cacheKey]: state }
            setFilterOptions(filterOptionsRef.current)
        }
        setOptionState({ status: 'loading', values: [] })
        try {
            setOptionState({ status: 'ready', values: await loadFilterOptions(column.name) })
        } catch (error) {
            setOptionState({
                status: 'error',
                values: [],
                message: error instanceof Error ? error.message : 'Не удалось загрузить значения фильтра.',
            })
        }
    }

    const handleColumnVisibilityChange = (name: string) => {
        if (name === fixedColumnName) return

        const visible = visibleColumnNames.has(name)
            ? normalizedColumnSettings.visible.filter(currentName => currentName !== name)
            : [...normalizedColumnSettings.visible, name]
        persistColumnSettings({ ...normalizedColumnSettings, visible })
    }

    const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over) return
        persistColumnSettings(moveApplicationTableColumn(normalizedColumnSettings, String(active.id), String(over.id)))
    }

    const persistColumnWidth = (name: string, width: number) => {
        if (name === fixedColumnName) return
        persistColumnSettings({
            ...normalizedColumnSettings,
            widths: { ...normalizedColumnSettings.widths, [name]: Math.round(width) },
        })
    }

    const beginColumnResize = (event: ReactPointerEvent<HTMLSpanElement>, column: IApplicationTableColumn) => {
        if (column.name === fixedColumnName) return
        event.preventDefault()
        event.stopPropagation()
        resizeCleanupRef.current?.()

        const startX = event.clientX
        const startWidth = columnWidths[column.name]
        const minimumWidth = minimumColumnWidths[column.name] || getFallbackColumnMinimumWidth(column)
        let latestWidth = startWidth
        const previousCursor = document.body.style.cursor
        const previousUserSelect = document.body.style.userSelect

        const cleanup = () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', finishResize)
            window.removeEventListener('pointercancel', finishResize)
            document.body.style.cursor = previousCursor
            document.body.style.userSelect = previousUserSelect
            resizeCleanupRef.current = null
        }
        const handlePointerMove = (moveEvent: PointerEvent) => {
            latestWidth = Math.min(maxColumnWidth, Math.max(minimumWidth, Math.round(startWidth + moveEvent.clientX - startX)))
            setLiveColumnWidths(currentWidths => ({ ...currentWidths, [column.name]: latestWidth }))
        }
        const finishResize = () => {
            cleanup()
            setLiveColumnWidths(currentWidths => {
                const nextWidths = { ...currentWidths }
                delete nextWidths[column.name]
                return nextWidths
            })
            persistColumnWidth(column.name, latestWidth)
        }

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', finishResize)
        window.addEventListener('pointercancel', finishResize)
        resizeCleanupRef.current = cleanup
    }

    const handleColumnResizeKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, column: IApplicationTableColumn) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return
        event.preventDefault()
        event.stopPropagation()

        const minimumWidth = minimumColumnWidths[column.name] || getFallbackColumnMinimumWidth(column)
        const currentWidth = columnWidths[column.name]
        const nextWidth = event.key === 'Home' ? minimumWidth
            : Math.min(maxColumnWidth, Math.max(minimumWidth, currentWidth + (event.key === 'ArrowRight' ? resizeKeyboardStep : -resizeKeyboardStep)))
        persistColumnWidth(column.name, nextWidth)
    }

    const clearFilters = () => {
        closeFilter()
        closeFilterEditor()
        if (hasFilters) onClearFilters()
    }

    const syncScroll = (source: HTMLDivElement, target: HTMLDivElement | null) => {
        if (target && target.scrollLeft !== source.scrollLeft) target.scrollLeft = source.scrollLeft
        setOpenMenu(null)
        closeFilter()
        closeColumnsSettings()
    }

    const renderFilter = (column: IApplicationTableColumn, placement: 'bottomLeft' | 'bottomRight') => {
        const filterColumn = column.name === 'client_name'
            ? columns.find(candidate => candidate.name === clientFilterName) || column
            : column
        const optionState = filterOptions[`${loginName}\u0000${column.name}`]
        const usesDateRange = dateRangeColumnNames.has(column.name)
        const usesSelect = column.type === 'boolean' || column.filterType === 'select'
        const selectOptions = column.type === 'boolean'
            ? [{ value: '1', label: 'Да' }, { value: '0', label: 'Нет' }]
            : (optionState?.values || []).map(value => ({ value, label: value }))
        const selectIsUnavailable = column.filterType === 'select' && optionState?.status !== 'ready'
        const isFilterActive = column.name === 'client_name'
            ? clientFilterNames.some(name => filters[name]?.length)
            : Boolean(filters[column.name]?.length)
        const dateRangeFrom = dateRangeDraft.from ? moment(dateRangeDraft.from, dateRangeDisplayFormat, true) : null
        const dateRangeTo = dateRangeDraft.to ? moment(dateRangeDraft.to, dateRangeDisplayFormat, true) : null
        const dateRangeHasInvalidValue = Boolean((dateRangeFrom && !dateRangeFrom.isValid()) || (dateRangeTo && !dateRangeTo.isValid()))
        const dateRangeValues = serializeDateRange(dateRangeDraft)
        const dateRangeIsInvalid = Boolean(!dateRangeHasInvalidValue && dateRangeFrom && dateRangeTo && dateRangeFrom.isAfter(dateRangeTo, 'day'))
        const dateRangeIsDisabled = !dateRangeValues.length || dateRangeHasInvalidValue || dateRangeIsInvalid || isAccessDenied || isLoading

        return <Popover trigger="click" open={openFilter === column.name}
        placement={placement} autoAdjustOverflow={{ shiftX: true, shiftY: true, adjustX: false, adjustY: false }} arrow={{ pointAtCenter: false }} getPopupContainer={() => document.body}
        classNames={{ root: css.filterPopup }} styles={{
            root: { '--filter-popup-width': `${filterPopupWidth}px` } as CSSProperties,
            arrow: { left: 'var(--arrow-x)', transform: 'translate(-50%, -100%)' },
        }} destroyOnHidden
        onOpenChange={open => {
            if (open) {
                closeFilterEditor(); setFilterPopupWidth(getFilterPopupWidth())
                setOpenFilter(column.name); setFilterDraft(''); setDateRangeDraft(parseDateRangeValues(filters[column.name])); setOpenMenu(null); setColumnsSettingsIsOpen(false)
                void ensureFilterOptions(column)
            }
            else if (openFilter === column.name) closeFilter()
        }}
        afterOpenChange={open => {
            if (open) filterFormRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]), [role="combobox"]')?.focus()
        }}
        content={<form ref={filterFormRef} className={css.filterForm} role="dialog"
            id={`${inputPrefix}-${column.name}-dialog`} aria-labelledby={`${inputPrefix}-${column.name}-title`}
            onKeyDown={event => {
                if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeFilter(true) }
            }}
            onSubmit={event => {
                event.preventDefault()
                if (usesDateRange) {
                    if (dateRangeIsDisabled) return
                    onFilterChange(column.name, dateRangeValues)
                    closeFilter(true)
                    return
                }
                if (!draftValue || isDuplicate || isFilterLimitReached || isAccessDenied || isLoading) return
                if (filterColumn.type === 'number' && !/^-?\d+(\.\d+)?$/.test(draftValue)) return
                if (column.filterType === 'select' && !optionState?.values.includes(draftValue)) return
                onFilterChange(filterColumn.name, [...(filters[filterColumn.name] || []), draftValue])
                closeFilter(true)
            }}>
            <div className={css.filterHeading}>
                <span id={`${inputPrefix}-${column.name}-title`}>{getLabel(column)}</span>
                <button type="button" className={css.iconButton} aria-label="Закрыть фильтр" onClick={() => closeFilter(true)}><CloseOutlined /></button>
            </div>
            {usesDateRange
                ? <>
                    <div className={css.dateRangeFields}>
                        <DateRangeField label="Начало" columnLabel={getLabel(column)} value={dateRangeDraft.from}
                            isDisabled={isAccessDenied || isLoading}
                            onChange={value => setDateRangeDraft(current => ({ ...current, from: value }))} />
                        <DateRangeField label="Окончание" columnLabel={getLabel(column)} value={dateRangeDraft.to}
                            isDisabled={isAccessDenied || isLoading}
                            onChange={value => setDateRangeDraft(current => ({ ...current, to: value }))} />
                    </div>
                    <div className={css.dateRangePresets} aria-label="Быстрый выбор периода">
                        <button type="button" disabled={isAccessDenied || isLoading} onClick={() => setDateRangeDraft(getCurrentDateRange('week'))}>Тек. неделя</button>
                        <button type="button" disabled={isAccessDenied || isLoading} onClick={() => setDateRangeDraft(getCurrentDateRange('month'))}>Тек. месяц</button>
                    </div>
                </>
                : usesSelect
                ? <div className={`${inputCss.primary} ${inputCss.md} ${css.filterField} ${(isAccessDenied || isLoading || selectIsUnavailable) ? inputCss.disabled : ''}`}>
                    <Select id={`${inputPrefix}-${column.name}`} aria-label={`Фильтр: ${getLabel(filterColumn)}`}
                        className={css.filterSelect} classNames={{ popup: { root: css.filterSelectPopup } }}
                        value={filterDraft || undefined} disabled={isAccessDenied || isLoading || selectIsUnavailable}
                        loading={column.filterType === 'select' && optionState?.status === 'loading'}
                        options={selectOptions} placeholder={optionState?.status === 'loading' ? 'Загрузка...' : 'Выберите значение'}
                        showSearch={column.filterType === 'select'} optionFilterProp="label" virtual={false} variant="borderless"
                        getPopupContainer={() => document.body} onChange={value => setFilterDraft(value)} />
                </div>
                : <div className={`${inputCss.primary} ${inputCss.md} ${css.filterField} ${(isAccessDenied || isLoading) ? inputCss.disabled : ''}`}>
                    <input id={`${inputPrefix}-${column.name}`} className={inputCss.md}
                    aria-label={`Фильтр: ${getLabel(filterColumn)}`} value={filterDraft} disabled={isAccessDenied || isLoading}
                    type={filterColumn.type === 'number' ? 'number' : 'text'} step={filterColumn.type === 'number' ? 'any' : undefined}
                    placeholder={filterColumn.type === 'date' || filterColumn.type === 'datetime' ? 'ДД.ММ.ГГГГ' : `Введите ${getLabel(filterColumn).toLocaleLowerCase('ru-RU')}`} maxLength={500} required
                    onChange={event => setFilterDraft(filterColumn.type === 'number' && event.target.value ? String(event.target.valueAsNumber) : event.target.value)} />
                </div>}
            {column.name === 'client_name' && <div className={css.clientFilterModes} role="radiogroup" aria-label="Поле поиска клиента">
                {clientFilterOptions.map(option => <button key={option.name} type="button" role="radio"
                    className={`${css.clientFilterMode} ${clientFilterName === option.name ? css.clientFilterModeActive : ''}`}
                    aria-checked={clientFilterName === option.name} disabled={isAccessDenied || isLoading}
                    onClick={() => { setClientFilterName(option.name); setFilterDraft('') }}>{option.label}</button>)}
            </div>}
            {optionState?.status === 'error' && <span className={css.filterNotice} role="status">{optionState.message}</span>}
            {dateRangeHasInvalidValue && <span className={css.filterNotice} role="status">Введите дату полностью: ДД.ММ.ГГГГ</span>}
            {dateRangeIsInvalid && <span className={css.filterNotice} role="status">Начало не может быть позже окончания</span>}
            {(isDuplicate || isFilterLimitReached) && <span className={css.filterNotice} role="status">{isDuplicate ? 'Это значение уже добавлено' : 'Достигнут лимит: 100 значений'}</span>}
            <Button type="submit" size="sm" isDisabled={usesDateRange ? dateRangeIsDisabled : !draftValue || isDuplicate || isFilterLimitReached || isAccessDenied || isLoading || selectIsUnavailable}>
                {!usesDateRange && <PlusOutlined aria-hidden="true" />}{usesDateRange ? 'Применить' : 'Добавить'}
            </Button>
        </form>}>
        <button type="button" className={`${isFilterActive || openFilter === column.name ? buttonCss.iconPrimary : buttonCss.icon} ${buttonCss.xs} ${css.systemIconButton} ${css.filterButton} ${isFilterActive ? css.filterActive : ''}`}
            ref={element => { if (element) filterTriggers.current.set(column.name, element); else filterTriggers.current.delete(column.name) }}
            aria-label={`Открыть фильтр: ${getLabel(column)}`} aria-haspopup="dialog" aria-expanded={openFilter === column.name}
            aria-controls={openFilter === column.name ? `${inputPrefix}-${column.name}-dialog` : undefined} disabled={isAccessDenied}
            title={`Фильтр: ${getLabel(column)}`}><IconFilter width="16px" height="16px" /></button>
            </Popover>
            }

    const columnsSettingsContent = <section className={css.columnsSettings} role="dialog" aria-labelledby={`${inputPrefix}-columns-settings-title`}
        onKeyDown={event => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeColumnsSettings(true) }
        }}>
        <div className={css.filterHeading}>
            <span id={`${inputPrefix}-columns-settings-title`}>Настройка столбцов</span>
            <button type="button" className={css.iconButton} aria-label="Закрыть настройки столбцов" onClick={() => closeColumnsSettings(true)}><CloseOutlined /></button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
            <div className={css.columnsList}>
                <SortableContext items={settingsColumns.map(column => column.name)} strategy={verticalListSortingStrategy}>
                    {settingsColumns.map(column => <SortableColumnOption key={column.name} column={column}
                        isSelected={visibleColumnNames.has(column.name)} isDisabled={isAccessDenied}
                        onChange={handleColumnVisibilityChange} />)}
                </SortableContext>
            </div>
        </DndContext>
    </section>

    const columnsSettingsControl = <Popover trigger="click" open={columnsSettingsIsOpen} placement="bottomLeft" arrow={{ pointAtCenter: true }}
        getPopupContainer={() => document.body} align={{ overflow: { shiftX: true, shiftY: true, adjustY: true } }}
        classNames={{ root: css.columnsSettingsPopup }} destroyOnHidden content={columnsSettingsContent}
        onOpenChange={open => {
            setColumnsSettingsIsOpen(open)
            if (open) { closeFilter(); closeFilterEditor(); setOpenMenu(null) }
        }}>
        <div className={css.columnsSettingsTrigger} ref={columnsSettingsTriggerRef}>
            <ToolTip title={columnsSettingsIsOpen ? null : 'Настроить столбцы'} placement="bottomLeft">
                <Button variant="icon" size="sm" isDisabled={isAccessDenied}>
                    <SettingOutlined aria-hidden="true" />
                    <span className={css.visuallyHidden}>Настроить столбцы</span>
                </Button>
            </ToolTip>
        </div>
    </Popover>

    const getFilterLabel = (name: string) => {
        const column = columns.find(candidate => candidate.name === name)
        return column ? getLabel(column) : columnLabels[name] || name
    }

    const getFilterValue = (name: string, values: string[]) => {
        if (dateRangeColumnNames.has(name)) {
            const range = parseDateRangeValues(values)
            return [range.from && `Начало: ${formatDateFilterValue(range.from)}`, range.to && `Окончание: ${formatDateFilterValue(range.to)}`]
                .filter(Boolean).join('; ')
        }
        const value = values[0] || ''
        return columns.find(column => column.name === name)?.type === 'boolean' ? value === '1' ? 'Да' : 'Нет' : value
    }

    const renderFilterEditor = (entry: FilterEntry) => {
        const column = columns.find(candidate => candidate.name === entry.name)
        if (!column) return null

        const optionState = filterOptions[`${loginName}\u0000${entry.name}`]
        const usesDateRange = dateRangeColumnNames.has(entry.name)
        const usesSelect = column.type === 'boolean' || column.filterType === 'select'
        const selectOptions = column.type === 'boolean'
            ? [{ value: '1', label: 'Да' }, { value: '0', label: 'Нет' }]
            : (optionState?.values || []).map(value => ({ value, label: value }))
        const selectIsUnavailable = column.filterType === 'select' && optionState?.status !== 'ready'
        const value = editingFilterDraft.trim()
        const siblingValues = (filters[entry.name] || []).filter((_, valueIndex) => valueIndex !== entry.valueIndex)
        const isDuplicateValue = !usesDateRange && siblingValues.includes(value)
        const dateRangeFrom = editingDateRangeDraft.from ? moment(editingDateRangeDraft.from, dateRangeDisplayFormat, true) : null
        const dateRangeTo = editingDateRangeDraft.to ? moment(editingDateRangeDraft.to, dateRangeDisplayFormat, true) : null
        const dateRangeHasInvalidValue = Boolean((dateRangeFrom && !dateRangeFrom.isValid()) || (dateRangeTo && !dateRangeTo.isValid()))
        const dateRangeValues = serializeDateRange(editingDateRangeDraft)
        const originalDateRangeValues = serializeDateRange(parseDateRangeValues(entry.values))
        const dateRangeIsInvalid = Boolean(!dateRangeHasInvalidValue && dateRangeFrom && dateRangeTo && dateRangeFrom.isAfter(dateRangeTo, 'day'))
        const isUnchanged = usesDateRange
            ? dateRangeValues.length === originalDateRangeValues.length && dateRangeValues.every((current, index) => current === originalDateRangeValues[index])
            : value === entry.values[0]
        const numberIsInvalid = column.type === 'number' && !/^-?\d+(\.\d+)?$/.test(value)
        const selectValueIsInvalid = column.filterType === 'select' && !optionState?.values.includes(value)
        const isSaveDisabled = usesDateRange
            ? !dateRangeValues.length || dateRangeHasInvalidValue || dateRangeIsInvalid || isUnchanged || isAccessDenied || isLoading
            : !value || numberIsInvalid || selectValueIsInvalid || isDuplicateValue || isUnchanged || isAccessDenied || isLoading || selectIsUnavailable
        const titleId = `${inputPrefix}-${entry.name}-${entry.valueIndex}-editor-title`

        return <form ref={filterEditorFormRef} className={css.filterForm} role="dialog" aria-labelledby={titleId}
            onKeyDown={event => {
                if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeFilterEditor(true) }
            }}
            onSubmit={event => {
                event.preventDefault()
                if (isSaveDisabled) return
                if (usesDateRange) onFilterChange(entry.name, dateRangeValues)
                else onFilterChange(entry.name, (filters[entry.name] || []).map((current, valueIndex) => valueIndex === entry.valueIndex ? value : current))
                closeFilterEditor(true)
            }}>
            <div className={css.filterHeading}>
                <span id={titleId}>Редактирование: {getFilterLabel(entry.name)}</span>
                <button type="button" className={css.iconButton} aria-label="Закрыть редактирование фильтра" onClick={() => closeFilterEditor(true)}><CloseOutlined /></button>
            </div>
            {usesDateRange
                ? <>
                    <div className={css.dateRangeFields}>
                        <DateRangeField label="Начало" columnLabel={getFilterLabel(entry.name)} value={editingDateRangeDraft.from}
                            isDisabled={isAccessDenied || isLoading}
                            onChange={current => setEditingDateRangeDraft(range => ({ ...range, from: current }))} />
                        <DateRangeField label="Окончание" columnLabel={getFilterLabel(entry.name)} value={editingDateRangeDraft.to}
                            isDisabled={isAccessDenied || isLoading}
                            onChange={current => setEditingDateRangeDraft(range => ({ ...range, to: current }))} />
                    </div>
                    <div className={css.dateRangePresets} aria-label="Быстрый выбор периода">
                        <button type="button" disabled={isAccessDenied || isLoading} onClick={() => setEditingDateRangeDraft(getCurrentDateRange('week'))}>Тек. неделя</button>
                        <button type="button" disabled={isAccessDenied || isLoading} onClick={() => setEditingDateRangeDraft(getCurrentDateRange('month'))}>Тек. месяц</button>
                    </div>
                </>
                : usesSelect
                ? <div className={`${inputCss.primary} ${inputCss.md} ${css.filterField} ${(isAccessDenied || isLoading || selectIsUnavailable) ? inputCss.disabled : ''}`}>
                    <Select id={`${inputPrefix}-${entry.name}-${entry.valueIndex}-editor`} aria-label={`Редактировать: ${getFilterLabel(entry.name)}`}
                        className={css.filterSelect} classNames={{ popup: { root: css.filterSelectPopup } }}
                        value={editingFilterDraft || undefined} disabled={isAccessDenied || isLoading || selectIsUnavailable}
                        loading={column.filterType === 'select' && optionState?.status === 'loading'} options={selectOptions}
                        placeholder={optionState?.status === 'loading' ? 'Загрузка...' : 'Выберите значение'}
                        showSearch={column.filterType === 'select'} optionFilterProp="label" virtual={false} variant="borderless"
                        getPopupContainer={() => document.body} onChange={current => setEditingFilterDraft(current)} />
                </div>
                : <div className={`${inputCss.primary} ${inputCss.md} ${css.filterField} ${(isAccessDenied || isLoading) ? inputCss.disabled : ''}`}>
                    <input id={`${inputPrefix}-${entry.name}-${entry.valueIndex}-editor`} className={inputCss.md}
                        aria-label={`Редактировать: ${getFilterLabel(entry.name)}`} value={editingFilterDraft} disabled={isAccessDenied || isLoading}
                        type={column.type === 'number' ? 'number' : 'text'} step={column.type === 'number' ? 'any' : undefined}
                        placeholder={`Введите ${getFilterLabel(entry.name).toLocaleLowerCase('ru-RU')}`} maxLength={500} required
                        onChange={event => setEditingFilterDraft(column.type === 'number' && event.target.value ? String(event.target.valueAsNumber) : event.target.value)} />
                </div>}
            {optionState?.status === 'error' && <span className={css.filterNotice} role="status">{optionState.message}</span>}
            {dateRangeHasInvalidValue && <span className={css.filterNotice} role="status">Введите дату полностью: ДД.ММ.ГГГГ</span>}
            {dateRangeIsInvalid && <span className={css.filterNotice} role="status">Начало не может быть позже окончания</span>}
            {isDuplicateValue && <span className={css.filterNotice} role="status">Это значение уже используется</span>}
            {isUnchanged && <span className={`${css.filterNotice} ${css.filterNoticeMuted}`} role="status">Изменений нет</span>}
            <Button type="submit" size="sm" isDisabled={isSaveDisabled}>Сохранить</Button>
        </form>
    }

    const openContractCreation = (item: IApplicationsList) => {
        handleOpenModalConfirm(true, 'Создание договора', `Вы действительно хотите создать договор по заявке № ${item.applicationID}`, item.applicationID, item.dbName, 'createContract', 0)
    }

    const renderContractCountBadge = (count: number, className = '') => count > 0
        ? <span className={`${css.contractCountBadge} ${className}`} title={`Оформленных договоров: ${count}`}>{count}</span>
        : null

    const renderValue = (item: IApplicationsList, column: IApplicationTableColumn) => {
        const value = item.viewValues?.[column.name]
        const text = formatValue(value, column)
        if (column.name === 'date_create' && value !== null && value !== undefined && value !== '') {
            const [date, ...timeParts] = text.split(' ')
            const time = timeParts.join(' ')
            return <span className={css.dateTimeValue}><span>{date}</span>{time && <span>{time}</span>}</span>
        }
        if (column.name === 'date_next_call') {
            const dateValue = value ?? item.dateNextCall
            const timeValue = item.viewValues?.time_next_call ?? item.timeNextCall
            const date = formatValue(dateValue, column)
            const time = formatTimeValue(timeValue)
            if ((dateValue === null || dateValue === undefined || dateValue === '') && !time) return <span className={css.emptyValue}>-</span>
            return <span className={css.dateTimeValue}><span>{date}</span>{time && <span>{time}</span>}</span>
        }
        if (value === null || value === undefined || value === '') return <span className={css.emptyValue}>{text}</span>
        if (column.name === 'application_id') {
            const siteLogo = getSiteLogo(item.viewValues?.data_base || item.dbName)
            return <span className={css.applicationIdentity}>
                {siteLogo && <img className={css.siteLogo} src={siteLogo.src} alt={siteLogo.alt} />}
                <span className={css.applicationSummary}>
                    <button type="button" className={css.recordLink} onClick={event => {
                        event.stopPropagation()
                        handleOpenCardApplication(true, 'edit', item.applicationID)
                    }} aria-label={`Открыть заявку № ${item.applicationID}`}>{text}</button>
                    <Rate disabled size="small" value={item.clientRating} className={`custom-rate ${css.applicationRating}`}
                        aria-label={`Рейтинг клиента: ${item.clientRating} из 5`} />
                </span>
            </span>
        }
        if (column.name === 'client_name') {
            const phone = String(item.viewValues?.client_phone ?? item.clientPhone ?? '').trim()
            const email = String(item.viewValues?.client_email ?? item.clientEmail ?? '').trim()
            return <span className={css.clientIdentity}>
                <span className={css.clientNameLine}>
                    {renderContactAction(item)}
                    <span className={value === null || value === undefined || value === '' ? css.emptyValue : ''}>{text}</span>
                </span>
                {(phone || email) && <span className={css.clientContacts}>
                    {phone && <a className={`${css.clientContact} ${css.clientPhone}`} href={`tel:${phone.replace(/[^\d+]/g, '')}`} onClick={event => event.stopPropagation()}>
                        <PhoneOutlined aria-hidden="true" />
                        <span>{phone}</span>
                    </a>}
                    {email && <a className={`${css.clientContact} ${css.clientEmail}`} href={`mailto:${email}`} onClick={event => event.stopPropagation()}>
                        <MailOutlined aria-hidden="true" />
                        <span>{email}</span>
                    </a>}
                </span>}
            </span>
        }
        if (column.name === 'application_status_name') {
            if (item.applicationStatusID !== 3) return <span className={css.status}>{text}</span>
            return <button type="button" className={`${css.status} ${css.statusAction}`}
                aria-label={`Создать договор по заявке № ${item.applicationID}. Оформленных договоров: ${item.contractCount}`}
                title="Создать договор" onClick={event => { event.stopPropagation(); openContractCreation(item) }}>
                <span>{text}</span>
                {renderContractCountBadge(item.contractCount, css.statusBadge)}
            </button>
        }
        if (column.type === 'boolean') return <span className={`${css.flag} ${Number(value) === 1 ? css.flagYes : ''}`}>{text}</span>
        if (column.name === 'page_canonical') {
            const href = getExternalUrl(value)
            return href ? <a className={css.landingLink} href={href} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>
                <LinkOutlined aria-hidden="true" />
                <span>{text}</span>
            </a> : <span className={css.wrappedValue}>{text}</span>
        }
        if (column.name === 'requirement_name') return <span className={css.wrappedValue}>{text}</span>
        if (column.name === 'duration_education') return <span className={css.durationValue}>{formatEducationDuration(value, item.viewValues?.volume_hours ?? item.volumeHours)}</span>
        if (column.name === 'application_status_color') return <span className={css.site}><span className={css.swatch} style={{ backgroundColor: String(value) }} />{text}</span>
        if (column.name === 'client_phone') return <a href={`tel:${text.replace(/[^\d+]/g, '')}`} onClick={event => event.stopPropagation()}>{text}</a>
        if (column.name === 'client_email') return <a href={`mailto:${text}`} onClick={event => event.stopPropagation()}>{text}</a>
        if (/^https:\/\//i.test(text) && /url$/.test(column.name)) return <a href={text} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>{text}</a>
        return text
    }

    return <>
        {!isAccessDenied && (hasFilters || (draftColumn && draftValue)) && <section className={css.filterBar} aria-label="Условия фильтрации">
            <div className={css.filterClearAction}>
                <ToolTip title="Очистить все фильтры" placement="bottomLeft">
                    <Button variant="icon" size="sm" isDisabled={!hasFilters && !draftValue} onClick={clearFilters}>
                        <ClearOutlined aria-hidden="true" />
                        <span className={css.visuallyHidden}>Очистить все фильтры</span>
                    </Button>
                </ToolTip>
            </div>
            <div className={css.filterChips}>
                {filterEntries.map(entry => <span className={css.filterChip} key={`${entry.name}-${entry.key}`}>
                    <Popover trigger="click" open={editingFilterKey === entry.editorKey} placement="bottomLeft"
                        autoAdjustOverflow={{ shiftX: true, shiftY: true, adjustX: false, adjustY: false }} arrow={{ pointAtCenter: false }} getPopupContainer={() => document.body}
                        classNames={{ root: css.filterPopup }} styles={{
                            root: { '--filter-popup-width': `${filterPopupWidth}px` } as CSSProperties,
                            arrow: { left: 'var(--arrow-x)', transform: 'translate(-50%, -100%)' },
                        }} destroyOnHidden content={renderFilterEditor(entry)}
                        onOpenChange={open => {
                            if (open) {
                                const column = columns.find(candidate => candidate.name === entry.name)
                                closeFilter(); setOpenMenu(null); setColumnsSettingsIsOpen(false); setFilterPopupWidth(getFilterPopupWidth())
                                setEditingFilterKey(entry.editorKey); setEditingFilterDraft(entry.values[0] || ''); setEditingDateRangeDraft(parseDateRangeValues(entry.values))
                                if (column) void ensureFilterOptions(column)
                            } else if (editingFilterKey === entry.editorKey) closeFilterEditor()
                        }}
                        afterOpenChange={open => {
                            if (open) filterEditorFormRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]), [role="combobox"]')?.focus()
                        }}>
                        <button type="button" className={css.filterChipEditButton}
                            ref={element => { if (element) filterEditorTriggers.current.set(entry.editorKey, element); else filterEditorTriggers.current.delete(entry.editorKey) }}
                            aria-label={`Редактировать фильтр: ${getFilterLabel(entry.name)}: ${getFilterValue(entry.name, entry.values)}`}
                            aria-haspopup="dialog" aria-expanded={editingFilterKey === entry.editorKey} title="Редактировать фильтр">
                            <span className={css.filterChipText}><strong>{getFilterLabel(entry.name)}:</strong> {getFilterValue(entry.name, entry.values)}</span>
                            <EditOutlined className={css.filterChipEditIcon} aria-hidden="true" />
                        </button>
                    </Popover>
                    <button type="button" className={css.iconButton} aria-label={`Удалить фильтр: ${getFilterLabel(entry.name)}: ${getFilterValue(entry.name, entry.values)}`}
                        onClick={() => { closeFilterEditor(); onFilterChange(entry.name, dateRangeColumnNames.has(entry.name) ? [] : filters[entry.name].filter((_, valueIndex) => valueIndex !== entry.valueIndex)) }}><CloseOutlined /></button>
                </span>)}
                {draftColumn && draftValue && <span className={`${css.filterChip} ${css.filterDraft}`}>
                    <span className={css.filterChipText}><strong>{getLabel(draftColumn)}:</strong> {getFilterValue(draftColumn.name, [draftValue])} <small>Не добавлен</small></span>
                    <button type="button" className={css.iconButton} aria-label={`Удалить черновик: ${getLabel(draftColumn)}`} onClick={() => setFilterDraft('')}><CloseOutlined /></button>
                </span>}
            </div>
        </section>}
        <section className={css.shell} aria-label="Табличный вид заявок" aria-busy={isLoading}>
        <Loader isOpen={isLoading} backdrop borderRadius="8px" color="var(--foreground_accent_default)" title="Загрузка..." />
        <div className={css.viewport} ref={viewportRef} tabIndex={0} role="region" aria-label="Таблица заявок"
            onScroll={event => syncScroll(event.currentTarget, scrollbarRef.current)}>
            <table className={`${css.table} ${css.fixedLayout}`} ref={tableRef} aria-label="Заявки"
                style={{ width: configuredTableWidth, minWidth: '100%' }}>
                <colgroup>
                    <col style={{ width: actionsColumnWidth }} />
                    {orderedColumns.map(column => <col key={column.name} data-column-name={column.name} style={{ width: columnWidths[column.name] }} />)}
                </colgroup>
                <thead><tr>
                    <th className={css.actionsCell} scope="col">{columnsSettingsControl}</th>
                    {orderedColumns.map((column, columnIndex) => <th key={column.name} scope="col"
                        ref={element => { if (element) columnHeaders.current.set(column.name, element); else columnHeaders.current.delete(column.name) }}
                        className={`${column.name === fixedColumnName ? css.idCell : css.resizableHeader}`}>
                        <div className={css.columnLabel}>
                            {renderFilter(column, columnIndex >= orderedColumns.length - 2 ? 'bottomRight' : 'bottomLeft')}
                            <ToolTip title={column.name} placement="bottomLeft"><span data-column-title>{getLabel(column)}</span></ToolTip>
                        </div>
                        {column.name !== fixedColumnName && <span role="separator" tabIndex={0} aria-orientation="vertical" className={css.resizeHandle}
                            aria-label={`Изменить ширину столбца «${getLabel(column)}»`}
                            aria-valuemin={minimumColumnWidths[column.name] || getFallbackColumnMinimumWidth(column)}
                            aria-valuemax={maxColumnWidth} aria-valuenow={columnWidths[column.name]}
                            title="Изменить ширину столбца" onPointerDown={event => beginColumnResize(event, column)}
                            onKeyDown={event => handleColumnResizeKeyDown(event, column)} />}
                    </th>)}
                </tr></thead>
                <tbody>{items.map(item => {
                    const delayedKey = `${item.applicationID}|${item.dateNextCall || ''}|${item.timeNextCall || ''}`
                    const isOverdue = item.isDelayedActionExpired === 1 && !viewedDelayedActionKeys.includes(delayedKey)
                    const menuItems = [
                        { key: 'contract', icon: <FileAddOutlined />, label: <span className={css.menuLabel}>Создать договор {renderContractCountBadge(item.contractCount)}</span>, disabled: item.applicationStatusID !== 3 },
                        { key: 'mailings', icon: <SendOutlined />, label: 'Рассылки' },
                        { key: 'blacklist', icon: item.isBlackList === 0 ? <StopOutlined /> : <RollbackOutlined />, label: item.isBlackList === 0 ? 'В чёрный лист' : 'Убрать из чёрного листа', danger: item.isBlackList === 0 },
                    ]
                    return <tr key={item.applicationID} className={isOverdue ? css.overdue : ''} style={{ '--row-accent': item.applicationStatusColor || 'var(--foreground_accent_default)' } as CSSProperties}
                        onClick={() => handleOpenCardApplication(true, 'edit', item.applicationID, isOverdue ? delayedKey : undefined)}>
                        <td className={css.actionsCell} onClick={event => event.stopPropagation()}>
                            <Dropdown trigger={['click']} open={openMenu === item.applicationID} onOpenChange={open => setOpenMenu(open ? item.applicationID : null)}
                                placement="bottomLeft" getPopupContainer={() => document.body} classNames={{ root: css.actionsPopup }} autoFocus destroyOnHidden
                                menu={{ items: menuItems, onKeyDown: event => {
                                    if (event.key === 'Escape') {
                                        setOpenMenu(null)
                                        menuTriggers.current.get(item.applicationID)?.focus()
                                    }
                                }, onClick: ({ key, domEvent }) => {
                                    domEvent.stopPropagation()
                                    setOpenMenu(null)
                                    if (key === 'contract') openContractCreation(item)
                                    if (key === 'mailings') handleOpenModalMailings(true, item.applicationID)
                                    if (key === 'blacklist') handleOpenModalConfirm(true, 'Чёрный лист', item.isBlackList === 0
                                        ? `Вы действительно хотите отправить заявку № ${item.applicationID} в чёрный лист?`
                                        : `Вы действительно хотите удалить заявку № ${item.applicationID} из чёрного листа?`, item.applicationID, item.dbName, 'toBlackList', item.isBlackList)
                                } }}>
                                <button type="button" className={`${openMenu === item.applicationID ? buttonCss.iconPrimary : buttonCss.icon} ${buttonCss.sm} ${css.systemIconButton} ${css.menuButton}`}
                                    ref={element => { if (element) menuTriggers.current.set(item.applicationID, element); else menuTriggers.current.delete(item.applicationID) }}
                                    aria-label={`Действия по заявке № ${item.applicationID}`} aria-haspopup="menu" aria-expanded={openMenu === item.applicationID}
                                    onKeyDown={event => {
                                        if (event.key === 'ArrowDown') { event.preventDefault(); setOpenMenu(item.applicationID) }
                                        if (event.key === 'Escape') setOpenMenu(null)
                                    }}>
                                    <MoreOutlined />
                                    {renderContractCountBadge(item.contractCount, css.menuButtonBadge)}
                                </button>
                            </Dropdown>
                            {isOverdue && <span className={css.overdueDot} role="img" aria-label="Просроченное действие" />}
                        </td>
                        {orderedColumns.map(column => <td key={column.name} className={`${column.name === 'application_id' ? css.idCell : ''} ${column.type === 'number' ? css.numeric : ''} ${centeredColumnNames.has(column.name) ? css.centeredCell : ''} ${column.name === 'application_status_name' ? css.statusCell : ''}`}>{renderValue(item, column)}</td>)}
                    </tr>
                })}</tbody>
            </table>
            {items.length === 0 && <div className={css.empty}><Empty image={isAccessDenied ? imgAccessDenied : imgEmpty} title={isAccessDenied ? 'Доступ запрещён' : 'Данные отсутствуют'} /></div>}
        </div>
        <div className={css.horizontalScroll} ref={scrollbarRef} tabIndex={0} role="region" aria-label="Горизонтальная прокрутка заявок"
            onScroll={event => syncScroll(event.currentTarget, viewportRef.current)}>
            <div style={{ width: tableWidth || '100%', minWidth: '100%', height: 1 }} />
        </div>
        </section>
    </>
}
